import React, { useState } from 'react';
import { Link as LinkR } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Bio } from '../data/constants';
import { FaBars } from 'react-icons/fa';

const Nav = styled.div`
  background-color: ${({ theme }) => theme.bg};
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  position: sticky;
  top: 0;
  z-index: 10;
  color: white;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
  border-bottom: 1px solid ${({ theme }) => theme.primary + '20'};
`;

const ColorText = styled.span`
  color: ${({ theme }) => theme.primary};
  font-family: 'Courier New', monospace;
  text-shadow: 0 0 5px ${({ theme }) => theme.primary + '50'};
  margin: 0 4px;
  position: relative;

  &:first-child {
    margin-right: 0;
  }

  &:last-child {
    margin-left: 0;
  }
`;

const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  z-index: 1;
  width: 100%;
  padding: 0 24px;
  max-width: 1200px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }
`;

const NavLogo = styled(LinkR)`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-left: 3px solid ${({ theme }) => theme.primary};
  position: relative;
  overflow: hidden;
  z-index: 10;
  letter-spacing: 1px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.5s;
  }

  &:hover:before {
    transform: translateX(100%);
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }
`;

const RainEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  pointer-events: none;
  overflow: hidden;

  &:after {
    content: '01010110 10101010 01100101 01110010';
    position: absolute;
    top: -10px;
    left: 0;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.primary};
    font-family: 'Courier New', monospace;
    font-size: 12px;
    letter-spacing: 2px;
    transform: rotate(30deg);
    user-select: none;
  }
`;

const TerminalPrompt = styled.div`
  display: none;
  align-items: center;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #63ff00;
  margin-left: 20px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5) inset;

  @media (min-width: 768px) {
    display: flex;
  }

  &:before {
    content: '$';
    margin-right: 8px;
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
  }
`;

const BlinkingCursor = styled.span`
  animation: blink 1.2s infinite;
  font-weight: bold;
  margin-left: 4px;

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const NavItems = styled.ul`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 0 6px;
  list-style: none;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  padding: 5px 10px;
  margin: 0 5px;
  font-size: 1rem;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
  }

  &:before {
    content: '>';
    opacity: 0;
    margin-right: 5px;
    color: ${({ theme }) => theme.primary};
    transition: opacity 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.primary};

    &:after {
      width: 100%;
    }

    &:before {
      opacity: 1;
    }
  }

  &.active {
    color: ${({ theme }) => theme.primary};

    &:before {
      opacity: 1;
    }
  }
`;

const ButtonContainer = styled.div`
  width: 80%;
  height: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  padding: 0 6px;
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const GitHubButton = styled.a`
  border: 1px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  justify-content: center;
  display: flex;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  padding: 10px 20px;
  margin-left: 16px;
  margin-right: 30px;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Courier New', monospace;
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: 0.5s;
  }

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.text_primary};
    box-shadow: 0 0 10px ${({ theme }) => theme.primary + '70'};
    transform: translateY(-3px);

    &:before {
      left: 100%;
    }
  }
`;

const MobileIcon = styled.div`
  display: none;
  @media screen and (max-width: 960px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-100%, 60%);
    font-size: 1.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.primary};
    background: rgba(0, 0, 0, 0.2);
    padding: 0px;
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.4);
      transform: translate(-100%, 60%) scale(1.1);
    }
  }
`;

const MobileMenu = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 16px;
  padding: 0 6px;
  list-style: none;
  width: 100%;
  padding: 12px 40px 24px 40px;
  background: ${({ theme }) => theme.card_light + 99};
  position: absolute;
  top: 80px;
  right: 0;

  transition: all 0.6s ease-in-out;
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  border-radius: 0 0 20px 20px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  opacity: ${({ isOpen }) => (isOpen ? '100%' : '0')};
  z-index: ${({ isOpen }) => (isOpen ? '1000' : '-1000')};
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  return (
    <Nav>
      <RainEffect />
      <NavbarContainer>
        <NavLogo to="/">
          <ColorText>&lt;</ColorText>Tristan
          <div style={{ color: theme.primary }}>/</div>Nguyen
          <ColorText>&gt;</ColorText>
        </NavLogo>
        <TerminalPrompt>
          cd /tristannguyen-portfolio && ls
          <BlinkingCursor>_</BlinkingCursor>
        </TerminalPrompt>
        <MobileIcon>
          <FaBars
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          />
        </MobileIcon>
        <NavItems>
          <NavLink href="#About">About</NavLink>
          <NavLink href="#Skills">Skills</NavLink>
          <NavLink href="#Experience">Experience</NavLink>
          <NavLink href="#Projects">Projects</NavLink>
          <NavLink href="#Education">Education</NavLink>
          <NavLink href="#Contact">Contact</NavLink>
        </NavItems>
        {isOpen && (
          <MobileMenu isOpen={isOpen}>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#About">
              About
            </NavLink>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#Skills">
              Skills
            </NavLink>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#Experience">
              Experience
            </NavLink>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#Projects">
              Projects
            </NavLink>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#Education">
              Education
            </NavLink>
            <NavLink onClick={() => setIsOpen(!isOpen)} href="#Contact">
              Contact
            </NavLink>
            <GitHubButton
              href={Bio.github}
              target="_blank"
              style={{
                padding: '10px 16px',
                background: `${theme.primary}`,
                color: 'white',
                width: 'max-content',
              }}
            >
              GitHub Profile
            </GitHubButton>
          </MobileMenu>
        )}
        <ButtonContainer>
          <GitHubButton href={Bio.github} target="_blank">
            GitHub Profile
          </GitHubButton>
        </ButtonContainer>
      </NavbarContainer>
    </Nav>
  );
};

export default Navbar;
